import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cachedGet, fileUrl, showSlug } from "@/lib/api";
import Reveal from "@/components/Reveal";

function fmt(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return { day: "--", month: "TBA", year: "" };
  return {
    day: d.toLocaleDateString("en-IN", { day: "2-digit" }),
    month: d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase(),
    year: d.getFullYear(),
  };
}

export const SectionTitle = ({ children, linkText, linkTo, testid }) => (
  <div className="flex items-end justify-between gap-6 border-b border-bone/10 pb-4">
    <h2 className="flex-1 text-center font-display text-xl sm:text-2xl tracking-[0.2em] text-bone" data-testid={testid}>
      <span className="section-ornament mr-4">ꗃ</span>{children}<span className="section-ornament ml-4">ꗃ</span>
    </h2>
    {linkText && linkTo && (
      <Link to={linkTo} className="hidden sm:block text-[10px] font-bold tracking-[0.2em] text-gold hover:text-saffron transition-colors whitespace-nowrap" data-testid={testid ? `${testid}-view-all` : undefined}>
        {linkText} →
      </Link>
    )}
  </div>
);

export default function EventsGrid() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cachedGet("/shows").then(({ data }) => setShows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (!loading && shows.length === 0) return null;

  return (
    <section id="events" className="px-6 md:px-12 py-16 md:py-20" data-testid="events-section">
      <SectionTitle linkText="VIEW ALL EVENTS" linkTo="/upcomingshows" testid="events-heading">UPCOMING EVENTS</SectionTitle>
      {loading && <p className="py-14 text-center text-ash text-sm">Loading events…</p>}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {shows.slice(0, 4).map((s, i) => {
          const d = fmt(s.date);
          const from = s.tickets?.length ? Math.min(...s.tickets.map((t) => t.price)) : s.price;
          return (
            <Reveal key={s.id} delay={i * 0.07} y={26}>
              <div className="group rounded-xl overflow-hidden border border-bone/10 bg-surface hover:border-saffron/50 transition-colors" data-testid={`event-card-${i}`}>
                <div className="relative aspect-[4/3] overflow-hidden bg-ink">
                  {s.image_url && (
                    <img src={fileUrl(s.image_url)} alt={s.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.04] transition-[opacity,transform] duration-700" loading="lazy" />
                  )}
                  {i === 0 && (
                    <span className="absolute top-3 left-3 bg-gold text-ink text-[9px] font-bold tracking-[0.15em] px-2.5 py-1 rounded-sm">FEATURED</span>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="text-center shrink-0">
                      <p className="font-display text-3xl text-gold leading-none">{d.day}</p>
                      <p className="text-[9px] tracking-[0.15em] text-ash mt-1">{d.month}<br />{d.year}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-bone/90 leading-snug line-clamp-2">{s.venue}</p>
                      <p className="text-[11px] text-ash mt-1.5">{s.time} Onwards</p>
                    </div>
                  </div>
                  <p className="mt-3 text-xs font-semibold text-bone truncate">{s.name}</p>
                  <div className="mt-4 flex items-end justify-between">
                    {s.ticketed ? (
                      <div>
                        <p className="text-[8px] tracking-[0.2em] text-ash">STARTING FROM</p>
                        <p className="font-display text-xl text-bone">₹{from.toLocaleString("en-IN")}</p>
                      </div>
                    ) : (
                      <p className="text-[9px] tracking-[0.18em] text-ash uppercase self-end pb-1">Free / RSVP entry</p>
                    )}
                    {s.ticketed ? (
                      <Link to={`/book/${showSlug(s)}`}
                        className="px-5 py-2.5 bg-saffron text-ink text-[10px] font-bold tracking-[0.12em] rounded-full hover:bg-gold transition-colors"
                        data-testid={`event-book-btn-${i}`}>
                        BOOK NOW
                      </Link>
                    ) : (
                      <Link to="/upcomingshows"
                        className="px-5 py-2.5 border border-saffron/50 text-saffron text-[10px] font-bold tracking-[0.12em] rounded-full hover:bg-saffron hover:text-ink transition-colors"
                        data-testid={`event-details-btn-${i}`}>
                        DETAILS
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
